import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { FaKey, FaSyncAlt } from 'react-icons/fa'
import api from '../../../helpers/apiRequester'
import styles from './styles.module.scss'
import {
    ACCESS_KEYS_PATH,
    AccessMode,
    BatchSummary,
    Pill,
    batchAccessPath,
    batchLabel,
    formatDateTime,
    handleAdminApiError,
} from './shared'

interface Filters {
    accessMode: AccessMode | 'all'
    dateFrom: string
    dateTo: string
    search: string
}

const DEFAULT_FILTERS: Filters = { accessMode: 'raw', dateFrom: '', dateTo: '', search: '' }

/**
 * Level 1: one row per funding batch that has had documentPack traffic.
 * Clicking a row opens the access list for that batch.
 */
export const DocumentBundleAccessBatchesView = () => {
    const navigate = useNavigate()
    // Optional ?accessKeyId= (linked from the keys page) narrows the overview
    // to batches touched by one alias key.
    const [searchParams, setSearchParams] = useSearchParams()
    const accessKeyId = searchParams.get('accessKeyId') || ''
    const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS)
    const [applied, setApplied] = useState<Filters>(DEFAULT_FILTERS)
    const [batches, setBatches] = useState<BatchSummary[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [keyLabel, setKeyLabel] = useState<string | null>(null)

    useEffect(() => {
        if (!accessKeyId) {
            setKeyLabel(null)
            return
        }
        api({ url: '/document-access-keys', data: {} })
            .then(({ data }) => {
                const match = (data?.keys || []).find((k: { id: number }) => String(k.id) === accessKeyId)
                setKeyLabel(match?.label || `Key #${accessKeyId}`)
            })
            .catch(() => setKeyLabel(`Key #${accessKeyId}`))
    }, [accessKeyId])

    const load = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const { data } = await api({
                url: '/document-access-log-batches',
                data: {
                    accessMode: applied.accessMode,
                    accessKeyId: accessKeyId || undefined,
                    dateFrom: applied.dateFrom || undefined,
                    dateTo: applied.dateTo || undefined,
                    search: applied.search || undefined,
                },
            })
            if (!data?.success) throw new Error(data?.message || 'Failed to load batches')
            setBatches(data.batches || [])
        } catch (err: any) {
            if (handleAdminApiError(err, navigate)) return
            setError(err?.response?.data?.message || err?.message || 'Failed to load batches')
        } finally {
            setLoading(false)
        }
    }, [applied, accessKeyId, navigate])

    useEffect(() => {
        load()
    }, [load])

    const totals = useMemo(
        () =>
            batches.reduce(
                (acc, b) => ({
                    accesses: acc.accesses + b.total_accesses,
                    rawSuccess: acc.rawSuccess + b.raw_success,
                    rawFailures: acc.rawFailures + b.raw_failures,
                    uniqueIps: acc.uniqueIps + b.unique_ips,
                }),
                { accesses: 0, rawSuccess: 0, rawFailures: 0, uniqueIps: 0 }
            ),
        [batches]
    )

    const onSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        setApplied(filters)
    }

    const onReset = () => {
        setFilters(DEFAULT_FILTERS)
        setApplied(DEFAULT_FILTERS)
    }

    const open = (b: BatchSummary) => navigate(batchAccessPath(b.batch_group))

    return (
        <div className={styles.wrap}>
            <div className={styles.introRow}>
                <p className={styles.intro}>
                    Every request for a funding-batch document bundle via <code>/document-link</code>, grouped by the
                    batch the bundle belongs to. Raw-key downloads are streamed through the API so successes, failures
                    and aborted transfers are all recorded.
                </p>
                <Link className={styles.secondaryBtn} to={ACCESS_KEYS_PATH}>
                    <FaKey /> Manage access keys
                </Link>
            </div>

            <div className={styles.statStrip}>
                <div className={styles.stat}>
                    <span className={styles.statValue}>{batches.length.toLocaleString('en-GB')}</span>
                    <span className={styles.statLabel}>Batches</span>
                </div>
                <div className={styles.stat}>
                    <span className={styles.statValue}>{totals.accesses.toLocaleString('en-GB')}</span>
                    <span className={styles.statLabel}>Accesses</span>
                </div>
                <div className={styles.stat}>
                    <span className={styles.statValue}>{totals.rawSuccess.toLocaleString('en-GB')}</span>
                    <span className={styles.statLabel}>Raw downloads</span>
                </div>
                <div className={styles.stat}>
                    <span className={`${styles.statValue} ${totals.rawFailures > 0 ? styles.bad : ''}`}>
                        {totals.rawFailures.toLocaleString('en-GB')}
                    </span>
                    <span className={styles.statLabel}>Raw failures</span>
                </div>
                <div className={styles.stat}>
                    <span className={styles.statValue}>{totals.uniqueIps.toLocaleString('en-GB')}</span>
                    <span className={styles.statLabel}>Unique IPs (sum)</span>
                </div>
            </div>

            <form className={styles.toolbar} onSubmit={onSubmit}>
                <input
                    className={styles.searchInput}
                    type="search"
                    placeholder="Filter by batch, introducer, IP or case ref…"
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                />
                <label className={styles.toolbarField}>
                    Mode
                    <select
                        className={styles.toolbarSelect}
                        value={filters.accessMode}
                        onChange={(e) => setFilters({ ...filters, accessMode: e.target.value as Filters['accessMode'] })}
                    >
                        <option value="raw">Raw key only</option>
                        <option value="viewer">Viewer only</option>
                        <option value="all">All modes</option>
                    </select>
                </label>
                <label className={styles.toolbarField}>
                    From
                    <input
                        className={styles.toolbarInput}
                        type="date"
                        value={filters.dateFrom}
                        onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                    />
                </label>
                <label className={styles.toolbarField}>
                    To
                    <input
                        className={styles.toolbarInput}
                        type="date"
                        value={filters.dateTo}
                        onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                    />
                </label>
                <div className={styles.toolbarActions}>
                    <button type="button" className={styles.secondaryBtn} onClick={onReset}>
                        Reset
                    </button>
                    <button type="submit" className={styles.secondaryBtn} disabled={loading}>
                        <FaSyncAlt /> Apply
                    </button>
                </div>
            </form>

            {accessKeyId && (
                <div className={styles.chipRow}>
                    <button
                        type="button"
                        className={`${styles.chipBtn} ${styles.active}`}
                        onClick={() => setSearchParams({})}
                        title="Clear the access key filter"
                    >
                        <Pill tone="blue">Key: {keyLabel || '…'}</Pill>
                        <span className={styles.chipCount}>×</span>
                    </button>
                </div>
            )}

            {error && (
                <div className={styles.errorBanner}>
                    {error}{' '}
                    <button type="button" className={styles.linkBtn} onClick={load}>
                        Retry
                    </button>
                </div>
            )}

            <p className={styles.resultLine}>
                {loading
                    ? 'Loading…'
                    : `Showing ${batches.length.toLocaleString('en-GB')} funding batches${
                          accessKeyId ? ' with downloads by this key' : ''
                      }`}
            </p>

            <div className={styles.tableWrap}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Funding batch</th>
                            <th>Raw downloads</th>
                            <th>Viewer</th>
                            <th>Reach</th>
                            <th>Keys</th>
                            <th>Activity</th>
                        </tr>
                    </thead>
                    <tbody>
                        {batches.length === 0 ? (
                            <tr>
                                <td colSpan={6}>
                                    <div className={styles.emptyState}>
                                        {loading
                                            ? 'Loading bundle access by batch…'
                                            : 'No bundle accesses match the current filters. Accesses appear here as soon as a document bundle link is opened.'}
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            batches.map((b) => (
                                <tr
                                    key={b.batch_key}
                                    className={styles.clickable}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => open(b)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault()
                                            open(b)
                                        }
                                    }}
                                >
                                    <td>
                                        <div className={styles.stackCell}>
                                            <span className={styles.stackPrimary}>
                                                {b.batch_group ? (
                                                    <span className={styles.refCode}>{b.batch_group}</span>
                                                ) : (
                                                    <span className={styles.muted}>{batchLabel(null)}</span>
                                                )}{' '}
                                                {b.documents_disabled && <Pill tone="warn">Disabled</Pill>}
                                            </span>
                                            <span className={styles.stackSecondary}>
                                                {b.introducer || <span className={styles.stackMeta}>No introducer</span>}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className={styles.stackCell}>
                                            <span className={styles.stackPrimary}>
                                                {b.raw_success.toLocaleString('en-GB')} ok
                                            </span>
                                            <span className={styles.stackSecondary}>
                                                <span className={b.raw_failures > 0 ? styles.bad : undefined}>
                                                    {b.raw_failures.toLocaleString('en-GB')} failed
                                                </span>
                                                {b.raw_in_progress > 0 && (
                                                    <span className={styles.stackMeta}>
                                                        · {b.raw_in_progress} in progress
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={styles.stackPrimary}>
                                            {b.viewer_accesses.toLocaleString('en-GB')}
                                        </span>
                                    </td>
                                    <td>
                                        <div className={styles.stackCell}>
                                            <span className={styles.stackPrimary}>
                                                {b.unique_ips.toLocaleString('en-GB')} IPs
                                            </span>
                                            <span className={styles.stackSecondary}>
                                                {b.unique_bundles.toLocaleString('en-GB')} bundles ·{' '}
                                                {b.total_accesses.toLocaleString('en-GB')} requests
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={styles.stackPrimary}>{b.unique_keys.toLocaleString('en-GB')}</span>
                                    </td>
                                    <td>
                                        <div className={styles.stackCell}>
                                            <span className={styles.stackPrimary}>{formatDateTime(b.last_access)}</span>
                                            <span className={styles.stackSecondary}>
                                                first {formatDateTime(b.first_access)}
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

export default DocumentBundleAccessBatchesView
